import "./gallery.css";

import React from "react";

import { GALLERY } from "../../data/content";
import { ChevronIcon } from "../general/Icons";
import PageMeta from "../general/PageMeta";

//Wix lays the photos out in blocks of five: two small on the left, one big in the middle, two small on the right
const tilePlacement = (i) => {
  const block = Math.floor(i / 5);
  const k = i % 5;
  const row = block * 2 + 1 + (k === 1 || k === 4 ? 1 : 0);
  return { className: `gallery-tile is-k${k}`, style: { "--row": row } };
};

//Full-screen photo viewer. Arrow keys / buttons move through the photos, Escape closes
const Lightbox = ({ index, onChange, onClose }) => {
  const photos = GALLERY.PHOTOS;
  const count = photos.length;
  const closeRef = React.useRef(null);

  React.useEffect(() => {
    document.body.style.overflow = "hidden";
    const opener = document.activeElement;
    closeRef.current.focus();
    return () => {
      document.body.style.overflow = "";
      if (opener) {
        opener.focus({ preventScroll: true });
      }
    };
  }, []);

  React.useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
      else if (e.key === "ArrowRight") {
        onChange((index + 1) % count);
      }
      else if (e.key === "ArrowLeft") {
        onChange((index - 1 + count) % count);
      }
      else if (e.key === "Tab") {
        //Keep focus on the viewer's buttons
        const buttons = [...document.querySelectorAll(".lightbox button")];
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
        else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [index, count, onChange, onClose]);

  const photo = photos[index];
  return (
    <div
      className={"lightbox"}
      role={"dialog"}
      aria-modal={"true"}
      aria-label={"Photo viewer"}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <button
        ref={closeRef}
        type={"button"}
        className={"lightbox-close"}
        aria-label={"Close"}
        onClick={onClose}
      >
        ×
      </button>
      <button
        type={"button"}
        className={"lightbox-arrow is-prev"}
        aria-label={"Previous photo"}
        onClick={() => onChange((index - 1 + count) % count)}
      >
        <ChevronIcon direction={"left"} />
      </button>
      <figure className={"lightbox-figure"}>
        <img
          key={photo.src}
          src={photo.src}
          alt={photo.alt}
        />
        <figcaption>
          {index + 1} / {count}
        </figcaption>
      </figure>
      <button
        type={"button"}
        className={"lightbox-arrow is-next"}
        aria-label={"Next photo"}
        onClick={() => onChange((index + 1) % count)}
      >
        <ChevronIcon />
      </button>
    </div>
  );
};

const Gallery = () => {
  const [openIndex, setOpenIndex] = React.useState(null);
  const close = React.useCallback(() => setOpenIndex(null), []);

  return (
    <section className={"gallery"}>
      <PageMeta
        title={GALLERY.TITLE}
        description={GALLERY.DESCRIPTION}
      />
      <h1 className={"gallery-title"}>{GALLERY.TITLE}</h1>

      <ul className={"gallery-grid"}>
        {GALLERY.PHOTOS.map((photo, i) => (
          <li
            key={photo.src}
            {...tilePlacement(i)}
          >
            <button
              type={"button"}
              onClick={() => setOpenIndex(i)}
              aria-label={`View photo: ${photo.alt}`}
            >
              <img
                src={photo.src}
                alt={""}
                loading={i < 5 ? "eager" : "lazy"}
              />
            </button>
          </li>
        ))}
      </ul>

      {openIndex !== null ? (
        <Lightbox
          index={openIndex}
          onChange={setOpenIndex}
          onClose={close}
        />
      ) : null}
    </section>
  );
};

export default Gallery;
